import React, { useCallback, useId, useMemo, useRef, useState } from "react"
import { useRouter } from "next/router"

import { Button } from "@coval/ui"
import { ChevronDown, ChevronRight, ChevronUp, RefreshCw } from "lucide-react"
import { useResizeObserver } from "usehooks-ts"

import {
  AudioPlayer,
  type AudioPlayerHandle,
} from "@/components/audio-player/AudioPlayer"
import { AudioTimelineCursor } from "@/components/audio-player/AudioTimelineCursor"
import { useAudioTimelineHover } from "@/components/audio-player/AudioTimelineHover"
import { useCompactAudioTrackInset } from "@/components/audio-player/useCompactAudioTrackInset"
import { chartBarSeriesColors } from "@/components/charts/seriesPalette"
import { JsonCodeEditor } from "@/components/JsonCodeEditor"

import { useSimulationDataFrames } from "../routes/useSimulationDataFrames"
import { expandFrameJson } from "../utils/expandFrameJson"
import {
  formatSimulationOffset,
  simulationDataFrameSpeaker,
  type SimulationDataFrameMark,
} from "../utils/simulationDataFrames"
import { AudioFrameOverlay, AudioFrameTracks } from "./AudioFrameTracks"
import { AudioFrameTracksSkeleton } from "./AudioFrameTracksSkeleton"
import {
  AudioTimedValueOverlay,
  AudioTimedValues,
  type AudioTimedValue,
} from "./AudioTimedValues"
import { AudioVisibilityMenu } from "./AudioVisibilityMenu"

const FRAME_TRACK_ROW_HEIGHT = 32
const MAX_FRAME_TRACK_HEIGHT = 224
const CHANNEL_TRACK_PADDING = 8
// Both branches draw the same frame, so callers never add their own border.
// The plain player leaves overflow visible so its marker overlay can rise above it.
const PLAYER_FRAME_CLASS =
  "min-w-0 rounded-md border border-coval-border bg-surface-default"

type ExpandableAudioPlayerProps = React.ComponentPropsWithoutRef<
  typeof AudioPlayer
> & {
  canInspectFrames: boolean
  timedValues?: AudioTimedValue[]
  /** A moment, in seconds, the pointer is over on another view of this recording. */
  hoverTimeSec?: number | null
  /** Hears the moment under the pointer on this player, and null once it leaves. */
  onHoverTimeChange?: (seconds: number | null) => void
  /**
   * Whether the Data Frames panel is open, for a host that holds that state
   * itself. Set, the toggle reads as a section header on the card's left
   * margin rather than a centred show/hide button.
   */
  framesExpanded?: boolean
  onFramesExpandedChange?: (expanded: boolean) => void
}

export function ExpandableAudioPlayer(props: ExpandableAudioPlayerProps) {
  // A different recording starts a new inspection, while expand/collapse keeps the player mounted.
  const router = useRouter()
  if (
    !props.canInspectFrames &&
    props.timedValues === undefined &&
    props.onHoverTimeChange === undefined &&
    props.hoverTimeSec === undefined
  ) {
    return (
      <div className={PLAYER_FRAME_CLASS}>
        <AudioPlayer {...props} />
      </div>
    )
  }
  return (
    <AudioFramePlayer
      key={`${router.asPath.split("?")[0]}:${props.recordingKey ?? props.simulationOutputId}`}
      {...props}
    />
  )
}

function AudioFramePlayer({
  canInspectFrames,
  timedValues,
  hoverTimeSec = null,
  onHoverTimeChange,
  framesExpanded,
  onFramesExpandedChange,
  ...props
}: ExpandableAudioPlayerProps) {
  const [waveformVisible, setWaveformVisible] = useState(true)
  const [localExpanded, setLocalExpanded] = useState(false)
  const isFramesControlled = framesExpanded !== undefined
  const requestedExpanded = framesExpanded ?? localExpanded
  const setExpanded = useCallback(
    (next: boolean | ((current: boolean) => boolean)) => {
      const value = typeof next === "function" ? next(requestedExpanded) : next
      if (isFramesControlled) {
        onFramesExpandedChange?.(value)
      } else {
        setLocalExpanded(value)
      }
    },
    [isFramesControlled, onFramesExpandedChange, requestedExpanded],
  )
  const expanded = canInspectFrames && requestedExpanded
  const [selectedId, setSelectedId] = useState<string>()
  const [currentTime, setCurrentTime] = useState(0)
  const [hasSpeakerLanes, setHasSpeakerLanes] = useState(false)
  const [decodedDuration, setDuration] = useState<number>()
  const duration = decodedDuration ?? props.audioDuration ?? 0
  const player = useRef<AudioPlayerHandle>(null)
  const pendingSeek = useRef<number | null>(null)
  const seekTo = useCallback((seconds: number) => {
    setCurrentTime(seconds)
    if (player.current) {
      pendingSeek.current = null
      player.current.seekTo(seconds)
    } else {
      pendingSeek.current = seconds
    }
  }, [])
  const root = useRef<HTMLDivElement>(null)
  const { width = 0 } = useResizeObserver({
    ref: root as React.RefObject<HTMLDivElement>,
  })
  const panelId = useId()
  const points = useMemo(
    () =>
      (timedValues ?? []).filter(
        (point) =>
          Number.isFinite(point.startSeconds) &&
          Number.isFinite(point.endSeconds) &&
          point.startSeconds >= 0 &&
          point.endSeconds >= point.startSeconds,
      ),
    [timedValues],
  )
  const frames = useSimulationDataFrames({
    simulationOutputId: props.simulationOutputId ?? "",
    enabled: Boolean(expanded && canInspectFrames && props.simulationOutputId),
  })
  const tables = useMemo(
    () => Object.keys(frames.rowsByTable).sort(),
    [frames.rowsByTable],
  )
  const colors = useMemo(
    () => chartBarSeriesColors(tables.length),
    [tables.length],
  )
  const marks = frames.marks
  const selected = useMemo(
    () => frames.marks.find((mark) => mark.id === selectedId),
    [frames.marks, selectedId],
  )
  const selectedJson = useMemo(
    () =>
      selected
        ? JSON.stringify(expandFrameJson(selected.row.frame), null, 2)
        : "",
    [selected],
  )
  const valueEndMs = points.reduce(
    (end, point) => Math.max(end, point.endSeconds * 1000),
    0,
  )
  const frameEndMs = useMemo(
    () => frames.marks.reduce((end, mark) => Math.max(end, mark.endMs), 0),
    [frames.marks],
  )
  const domainMs = Math.max(
    duration * 1000,
    expanded ? frameEndMs : 0,
    valueEndMs,
    1,
  )
  const compactInset = useCompactAudioTrackInset(
    root,
    Boolean(props.compact && decodedDuration),
    duration * 1000,
    domainMs,
  )
  const wellWidth = Math.max(1, width - 128 - 16)
  const tailPx =
    duration > 0 ? wellWidth * (1 - (duration * 1000) / domainMs) : 0
  const inset = props.trackInsetPx ??
    compactInset ?? {
      leftPx: 128,
      rightPx: 16 + tailPx,
      tailPx,
    }
  const externalWellWidth = Math.max(
    1,
    width - inset.leftPx - inset.rightPx + (inset.tailPx ?? 0),
  )
  const timelineDurationMs =
    props.trackInsetPx && duration > 0
      ? (duration * 1000 * externalWellWidth) /
        Math.max(1, externalWellWidth - (inset.tailPx ?? 0))
      : domainMs
  const timelineMarks = useMemo(
    () => marks.filter((mark) => mark.startMs <= timelineDurationMs),
    [marks, timelineDurationMs],
  )
  const outsideMarks = useMemo(
    () => marks.filter((mark) => mark.startMs > timelineDurationMs),
    [marks, timelineDurationMs],
  )
  const selectFrame = useCallback(
    (mark: SimulationDataFrameMark, seconds = mark.startMs / 1000) => {
      setSelectedId(mark.id)
      seekTo(seconds)
    },
    [seekTo],
  )
  const { onTimeUpdate: notifyTime, onReady: notifyReady } = props
  const onTimeUpdate = useCallback(
    (time: number) => {
      setCurrentTime(time)
      notifyTime?.(time)
    },
    [notifyTime],
  )
  const onReady = useCallback(() => {
    setHasSpeakerLanes(
      (player.current?.getDecodedData()?.numberOfChannels ?? 0) >= 2,
    )
    const loadedDuration = player.current?.getDuration()
    if (loadedDuration && Number.isFinite(loadedDuration)) {
      setDuration(loadedDuration)
    }
    if (pendingSeek.current !== null && player.current) {
      player.current.seekTo(pendingSeek.current)
      pendingSeek.current = null
    }
    notifyReady?.()
  }, [notifyReady])

  const splitFrames =
    hasSpeakerLanes && !props.compact && Boolean(props.signedUrl)
  const speakerMarks = useMemo(
    () =>
      ["agent", "persona"].map((speaker) =>
        timelineMarks.filter(
          (mark) => simulationDataFrameSpeaker(mark) === speaker,
        ),
      ),
    [timelineMarks],
  )
  const bottomMarks = useMemo(
    () =>
      splitFrames
        ? timelineMarks.filter(
            (mark) => simulationDataFrameSpeaker(mark) === null,
          )
        : timelineMarks,
    [splitFrames, timelineMarks],
  )
  const speakerTables = useMemo(
    () =>
      speakerMarks.map((items) => {
        const names = new Set(items.map((mark) => mark.tableName))
        return tables.filter((table) => names.has(table))
      }),
    [speakerMarks, tables],
  )
  const loadingFrames = expanded && frames.isLoading && !frames.error
  // Table names are not known until the first page arrives; reserve a small representative track area.
  const placeholderRows = marks.length === 0 ? 2 : 1
  const channelContentHeight =
    Math.min(
      MAX_FRAME_TRACK_HEIGHT,
      Math.max(
        ...speakerTables.map(
          (items) => items.length + (loadingFrames ? placeholderRows : 0),
        ),
      ) * FRAME_TRACK_ROW_HEIGHT,
    ) + CHANNEL_TRACK_PADDING
  const renderTracks = (
    items: SimulationDataFrameMark[],
    trackTables: string[],
    label?: string,
    showTimeAxis = true,
    minHeight = 0,
  ) => (
    <AudioFrameTracks
      marks={items}
      tables={trackTables}
      colors={trackTables.map((table) => colors[tables.indexOf(table)])}
      durationMs={timelineDurationMs}
      currentTime={currentTime}
      selectedId={selectedId}
      onSelect={selectFrame}
      onSeek={seekTo}
      minHeight={minHeight}
      inset={inset}
      label={label}
      showTimeAxis={showTimeAxis}
    />
  )
  const timelineLayoutKey = `${expanded}:${waveformVisible}:${timelineDurationMs}:${channelContentHeight}:${bottomMarks.length}:${points.length}:${width}:${props.signedUrl}:${compactInset?.leftPx}:${compactInset?.rightPx}`
  const timelineReady = Boolean(props.signedUrl && decodedDuration)
  const { preview, ...hoverHandlers } = useAudioTimelineHover(
    root,
    timelineReady,
    timelineLayoutKey,
    onHoverTimeChange,
  )
  const framesHint = props.signedUrl
    ? "Click anywhere on a track to seek. Click a frame to inspect its data."
    : "Select a frame to inspect its data."
  const options =
    canInspectFrames && props.simulationOutputId ? (
      <AudioVisibilityMenu
        waveformAvailable={Boolean(props.signedUrl)}
        waveformVisible={waveformVisible}
        onWaveformVisibleChange={setWaveformVisible}
        framesVisible={expanded}
        onFramesVisibleChange={setExpanded}
        panelId={panelId}
      />
    ) : null
  // A host-held toggle reads as a section header on the card's left margin.
  // With controls to sit beside, it shares their row; without, it takes its
  // own.
  const framesToggle =
    options && isFramesControlled ? (
      <button
        type="button"
        className="flex items-center gap-2 rounded-md text-sm font-medium text-coval-text hover:text-coval-text-muted"
        aria-expanded={expanded}
        aria-controls={panelId}
        onClick={() => setExpanded((value) => !value)}
      >
        {expanded ? (
          <ChevronDown className="h-4 w-4 text-coval-text-muted" />
        ) : (
          <ChevronRight className="h-4 w-4 text-coval-text-muted" />
        )}
        Data Frames
      </button>
    ) : null
  const framesToggleInControls = Boolean(
    framesToggle && props.signedUrl && !props.compact,
  )

  return (
    <div
      ref={root}
      className={`ph-mask relative overflow-hidden ${PLAYER_FRAME_CLASS}`}
      data-dd-privacy="mask"
      data-ph-capture="false"
      {...hoverHandlers}
    >
      <AudioTimelineCursor
        root={root}
        enabled={timelineReady}
        seconds={currentTime}
        layoutKey={timelineLayoutKey}
      />
      {preview ??
        (hoverTimeSec != null && (
          <AudioTimelineCursor
            root={root}
            enabled={timelineReady}
            seconds={hoverTimeSec}
            layoutKey={timelineLayoutKey}
            variant="hover"
          />
        ))}
      {props.signedUrl && (
        <AudioPlayer
          {...props}
          showHoverPreview={false}
          showPlaybackCursor={false}
          ref={player}
          waveformVisible={waveformVisible}
          controlsExtra={props.compact ? undefined : options}
          controlsLeading={framesToggleInControls ? framesToggle : undefined}
          channelWaveformOverlay={
            expanded && splitFrames
              ? speakerMarks.map((items) => (
                  <AudioFrameOverlay
                    key={items[0]?.id ?? "empty"}
                    marks={items}
                    tables={tables}
                    colors={colors}
                    durationMs={timelineDurationMs}
                    selectedId={selectedId}
                  />
                ))
              : undefined
          }
          channelContent={
            expanded &&
            splitFrames &&
            (loadingFrames || speakerTables.some((items) => items.length > 0))
              ? {
                  height: channelContentHeight,
                  lanes: speakerMarks.map((items, channel) => (
                    <div
                      key={channel}
                      className="overflow-y-auto"
                      style={{ maxHeight: channelContentHeight }}
                    >
                      {(!loadingFrames || items.length > 0) &&
                        renderTracks(
                          items,
                          speakerTables[channel],
                          channel === 0
                            ? "Agent Data Frame tracks"
                            : "Persona Data Frame tracks",
                          false,
                          loadingFrames
                            ? 0
                            : channelContentHeight - CHANNEL_TRACK_PADDING,
                        )}
                      {loadingFrames && (
                        <AudioFrameTracksSkeleton
                          inset={inset}
                          rows={placeholderRows}
                          label={
                            channel === 0
                              ? "Agent frame placeholders"
                              : "Persona frame placeholders"
                          }
                          continuation={items.length > 0}
                        />
                      )}
                    </div>
                  )),
                }
              : undefined
          }
          onTimeUpdate={onTimeUpdate}
          onReady={onReady}
          trackInsetPx={
            expanded || points.length > 0 ? inset : props.trackInsetPx
          }
          waveformOverlay={
            <>
              {props.waveformOverlay}
              {expanded && bottomMarks.length > 0 && (
                <AudioFrameOverlay
                  marks={bottomMarks}
                  tables={tables}
                  colors={colors}
                  durationMs={timelineDurationMs}
                  selectedId={selectedId}
                />
              )}
              {points.length > 0 && (
                <AudioTimedValueOverlay
                  points={points}
                  durationMs={timelineDurationMs}
                />
              )}
            </>
          }
        />
      )}
      {points.length > 0 && (
        <AudioTimedValues
          points={points}
          durationMs={timelineDurationMs}
          inset={inset}
          onSeek={seekTo}
        />
      )}
      {(!props.signedUrl || props.compact) && options && (
        <div className="flex justify-end px-3 pt-2">{options}</div>
      )}
      {framesToggle && !framesToggleInControls && (
        <div className="px-4 py-3">{framesToggle}</div>
      )}
      {options && !isFramesControlled && (
        <div className="flex justify-center">
          <Button
            type="button"
            variant="ghost"
            className="h-6 rounded-none text-xs [&>svg]:size-4"
            aria-expanded={expanded}
            aria-controls={panelId}
            onClick={() => setExpanded((value) => !value)}
          >
            {expanded ? <ChevronUp /> : <ChevronDown />}
            {expanded ? "Hide Data Frames" : "Show Data Frames"}
          </Button>
        </div>
      )}
      {expanded && (
        <div id={panelId} className="border-t border-coval-border">
          {isFramesControlled && marks.length > 0 && (
            <p className="px-4 pt-3 text-xs text-coval-text-muted">
              {framesHint}
            </p>
          )}
          {frames.error && (
            <div
              role="alert"
              className="flex items-center justify-between gap-2 px-4 py-3 text-sm text-coval-danger-on-surface"
            >
              {marks.length > 0 && !frames.hasCompleteFrameSet
                ? "Data Frames are incomplete. Only loaded frames are shown. Retry to load the remaining frames."
                : "Couldn't load Data Frames."}
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  void frames.refetch()
                }}
              >
                <RefreshCw />
                Retry frames
              </Button>
            </div>
          )}
          {loadingFrames && (
            <span
              role="status"
              aria-label={
                marks.length > 0
                  ? "Loading more Data Frames"
                  : "Loading Data Frames"
              }
              className="sr-only"
            >
              {marks.length > 0
                ? "Loading more Data Frames…"
                : "Loading Data Frames…"}
            </span>
          )}
          {frames.isTruncated && (
            <p
              role="status"
              className="px-4 pt-3 text-xs text-coval-text-muted"
            >
              Showing the first 10,000 frames. More frames are available for
              this simulation.
            </p>
          )}
          {frames.hasCompleteFrameSet &&
            !frames.error &&
            frames.marks.length === 0 && (
              <p
                role="status"
                className="px-4 py-6 text-sm text-coval-text-muted"
              >
                No timed Data Frames were found for this simulation.
              </p>
            )}
          {bottomMarks.length > 0 &&
            renderTracks(
              bottomMarks,
              tables.filter((table) =>
                bottomMarks.some((mark) => mark.tableName === table),
              ),
            )}
          {loadingFrames && (!splitFrames || bottomMarks.length > 0) && (
            <AudioFrameTracksSkeleton
              inset={inset}
              rows={placeholderRows}
              label="Data Frame placeholders"
              continuation={bottomMarks.length > 0}
            />
          )}
          {outsideMarks.length > 0 && (
            <div className="space-y-2 border-t border-coval-border px-4 py-3">
              <p className="text-xs text-coval-text-muted">
                {outsideMarks.length}{" "}
                {outsideMarks.length === 1 ? "frame is" : "frames are"} after
                the displayed timeline.
              </p>
              <div className="flex max-h-24 flex-wrap gap-1 overflow-y-auto">
                {outsideMarks.map((mark) => (
                  <Button
                    key={mark.id}
                    type="button"
                    variant="ghost"
                    aria-label={`${mark.tableName} frame at ${formatSimulationOffset(mark.startMs)}`}
                    onClick={() => selectFrame(mark)}
                  >
                    {mark.tableName} · {formatSimulationOffset(mark.startMs)}
                  </Button>
                ))}
              </div>
            </div>
          )}
          {selected ? (
            <div className="border-t border-coval-border bg-surface-1 p-3">
              {selected.startMs >= duration * 1000 && duration > 0 && (
                <p className="mb-2 text-xs text-coval-text-muted">
                  This frame is after the recording ends.
                </p>
              )}
              <JsonCodeEditor
                value={selectedJson}
                onChange={() => {}}
                readOnly
                height="180px"
                ariaLabel="Selected Data Frame payload"
              />
            </div>
          ) : marks.length > 0 && !isFramesControlled ? (
            <p className="border-t border-coval-border px-4 py-3 text-xs text-coval-text-muted">
              {framesHint}
            </p>
          ) : null}
        </div>
      )}
    </div>
  )
}
